import { useState, type FormEvent } from 'react';
import { adminApi, ApiError } from './admin-api.ts';
import { AdminShell, Card, GhostButton, PrimaryButton, Select, TextField } from './ui.tsx';
import type { AdminUser } from './types.ts';

// A date input gives YYYY-MM-DD; the API wants full ISO. Start = midnight, end = end-of-day (UTC),
// which also satisfies the schema's end > start check when both are the same day.
const startIso = (d: string) => (d ? `${d}T00:00:00.000Z` : null);
const endIso = (d: string) => (d ? `${d}T23:59:59.000Z` : null);
const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function EngagementForm({
  admin,
  onDone,
  onCancel,
  onLogout,
}: {
  admin: AdminUser;
  onDone: () => void;
  onCancel: () => void;
  onLogout: () => void;
}) {
  const [clientName, setClientName] = useState('');
  const [internalName, setInternalName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [surveyStart, setSurveyStart] = useState('');
  const [surveyEnd, setSurveyEnd] = useState('');
  const [status, setStatus] = useState('active');
  const [assessmentType, setAssessmentType] = useState<'full' | 'lightweight'>('full');
  const [welcomeCopy, setWelcomeCopy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // auto-fill slug from client name until the admin edits it directly
  const onClient = (v: string) => {
    setClientName(v);
    if (!slugEdited) setSlug(slugify(v));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await adminApi.createEngagement({
        clientName,
        internalName: internalName || `${clientName} DMA`,
        slug,
        status,
        assessmentType,
        surveyStart: startIso(surveyStart),
        surveyEnd: endIso(surveyEnd),
        welcomeCopy: welcomeCopy || null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the engagement.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell admin={admin} onLogout={onLogout}>
      <div className="mb-4">
        <GhostButton onClick={onCancel}>← Engagements</GhostButton>
      </div>
      <div className="mx-auto max-w-2xl">
        <Card title="New engagement" description="Create the survey engagement, then add its lines of business and respondents.">
          <form className="flex flex-col gap-4" onSubmit={submit}>
            <TextField id="client" label="Client name" value={clientName} onChange={onClient} required />
            <TextField id="internal" label="Internal name" value={internalName} onChange={setInternalName} />
            <TextField
              id="slug"
              label="Survey URL slug (e.g. 'corning' → /corning)"
              value={slug}
              onChange={(v) => {
                setSlugEdited(true);
                setSlug(slugify(v));
              }}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <TextField id="start" label="Survey opens" type="date" value={surveyStart} onChange={setSurveyStart} />
              <TextField id="end" label="Survey closes" type="date" value={surveyEnd} onChange={setSurveyEnd} />
            </div>
            <Select
              id="status"
              label="Status"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'ready', label: 'Ready' },
                { value: 'active', label: 'Active (respondents can log in)' },
                { value: 'closed', label: 'Closed' },
              ]}
            />
            <div>
              <span className="block text-sm font-medium text-ink-soft mb-1.5">
                Assessment type <span className="text-xs font-normal text-ink-soft">(cannot be changed after creation)</span>
              </span>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    { value: 'full' as const, label: 'Full Dimension', desc: 'The standard 15-dimension / 61-question assessment.' },
                    { value: 'lightweight' as const, label: 'Lightweight', desc: 'Data Questions + Department-Specific Questions only.' },
                  ]
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="cursor-pointer rounded-xl border p-3 text-left transition"
                    style={{
                      borderColor: assessmentType === opt.value ? 'var(--accent)' : 'rgba(0,0,0,0.10)',
                      background: assessmentType === opt.value ? 'var(--accent-soft)' : 'white',
                    }}
                  >
                    <input
                      type="radio"
                      name="assessmentType"
                      value={opt.value}
                      checked={assessmentType === opt.value}
                      onChange={() => setAssessmentType(opt.value)}
                      className="sr-only"
                    />
                    <span className="block text-sm font-semibold text-ink">{opt.label}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-ink-soft">{opt.desc}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
              Welcome message (optional)
              <textarea
                className="min-h-20 w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-base text-ink outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]"
                value={welcomeCopy}
                onChange={(e) => setWelcomeCopy(e.target.value)}
              />
            </label>
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            <div className="flex justify-end gap-3">
              <GhostButton onClick={onCancel}>Cancel</GhostButton>
              <PrimaryButton type="submit" disabled={busy || !clientName || !slug}>
                {busy ? 'Creating…' : 'Create engagement'}
              </PrimaryButton>
            </div>
          </form>
        </Card>
      </div>
    </AdminShell>
  );
}
